import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isTableColorPickerOpen } from "./tableColorPicker.js";
import { sanitizeCellHtml } from "./sanitizeCellHtml.js";
import { historyShortcut } from "../historyKeys.js";
import { useLatestRef } from "../useLatestRef.js";
import { typographyCssVars } from "./tableTypography.js";
import {
  FILLS,
  applyPaste,
  cellFill,
  clearRangeText,
  delCol,
  delRow,
  fillCSS,
  fillCSSPair,
  htmlToText,
  insCol,
  normalizeCellHtml,
  mixWithWhite,
  insRow,
  moveCol,
  moveRow,
  selectionCount,
  selectionRect,
  setCellFill,
  MAX_ROW_HEIGHT,
  MIN_ROW_HEIGHT,
  setCellHtml,
  setColFill,
  setRangeFill,
  setRowFill,
  setRowHeight,
  sortCol,
} from "./tableModel.js";
import { getTableSelection, setTableSelection } from "./tableSelection.js";
import { automaticRowHeight } from "./tableRowHeight.js";
import "./TableBlock.css";

const MOD = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘" : "Ctrl";

function CellView({ html, blank, editable, typing, label, placeholder, row, col, onInput, onFocus }) {
  const ref = useRef(null);
  const lastEmittedRef = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const next = sanitizeCellHtml(html || "");
    if (node.innerHTML === next) return;
    const focused = document.activeElement === node;
    // Typing already updated the DOM; rewriting it would reset the caret.
    if (focused && (html === lastEmittedRef.current || next === lastEmittedRef.current)) return;
    // Anything else (undo, redo, outside edit) must reach the screen, even when focused.
    node.innerHTML = next;
    lastEmittedRef.current = next;
    if (!focused) return;
    const selection = window.getSelection();
    if (!selection) return;
    const caret = document.createRange();
    caret.selectNodeContents(node);
    caret.collapse(false);
    selection.removeAllRanges();
    selection.addRange(caret);
  }, [html]);

  return (
    <div
      ref={ref}
      className={["cell", blank ? "is-blank" : "", typing ? "is-typing" : ""].filter(Boolean).join(" ")}
      contentEditable={editable}
      suppressContentEditableWarning
      role="textbox"
      spellCheck={false}
      data-r={row}
      data-c={col}
      data-ph={placeholder || undefined}
      aria-label={label}
      onFocus={onFocus}
      onInput={(event) => {
        const value = event.currentTarget.innerHTML;
        lastEmittedRef.current = normalizeCellHtml(value);
        onInput(value);
      }}
    />
  );
}

function FillPalette({ label, current, accent, onPick, onCustomPick }) {
  const [custom, setCustom] = useState(null);
  const shown = custom || current;
  return (
    <div className="palwrap">
      <span className="pallabel">{label}</span>
      <div className="pal" role="group" aria-label={label}>
        {FILLS.map((fill) => (
          <button
            key={fill.n}
            type="button"
            className={`fsw${fill.v === shown ? " on" : ""}${fill.v ? "" : " none"}`}
            style={{ "--c": fill.v ? fillCSS(fill.v, accent) : "transparent" }}
            title={fill.n}
            aria-label={fill.n}
            onClick={() => onPick(fill.v || null)}
          />
        ))}
        <span className="custom" style={{ "--c": shown ? fillCSS(shown, accent) : "#F6F1E6" }}>
          <input
            type="color"
            aria-label={`Pick any ${label.toLowerCase()}`}
            defaultValue={/^#[0-9a-f]{6}$/i.test(shown || "") ? shown : "#F6F1E6"}
            onChange={(event) => {
              setCustom(event.target.value);
              onCustomPick(event.target.value);
            }}
          />
        </span>
      </div>
    </div>
  );
}

export function menuBounds() {
  const margin = 8;
  let top = margin;
  let bottom = window.innerHeight - margin;
  const pageBar = document.querySelector("[data-newsletter-pagebar], [data-slide-pagebar]");
  const barTop = pageBar?.parentElement?.getBoundingClientRect?.().top;
  if (typeof barTop === "number" && barTop > margin) bottom = Math.min(bottom, barTop - margin);
  const toolbar = document.querySelector("[data-shared-editor-toolbar]");
  const toolbarBottom = toolbar?.getBoundingClientRect?.().bottom;
  if (typeof toolbarBottom === "number" && toolbarBottom > 0) top = Math.max(top, toolbarBottom + margin);
  return {
    top,
    bottom: Math.max(top + 120, bottom),
    left: margin,
    right: window.innerWidth - margin,
  };
}

function TableMenu({ items, point, onClose }) {
  const ref = useRef(null);
  const [box, setBox] = useState({ left: point.x, top: point.y, maxHeight: null });

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const bounds = menuBounds();
    const available = bounds.bottom - bounds.top;
    node.style.maxHeight = `${available}px`;
    const width = node.offsetWidth;
    const height = node.offsetHeight;
    let top = point.y;
    if (top + height > bounds.bottom) top = bounds.bottom - height;
    if (top < bounds.top) top = bounds.top;
    setBox({
      left: Math.max(bounds.left, Math.min(point.x, bounds.right - width)),
      top,
      maxHeight: available,
    });
    const first = node.querySelector("button.item:not(:disabled)");
    first?.focus();
    return undefined;
  }, [point.x, point.y]);

  useEffect(() => {
    const closeAfterColorPicker = () => {
      window.setTimeout(() => {
        if (isTableColorPickerOpen()) return;
        onClose();
      }, 0);
    };
    const onPointer = (event) => {
      const menu = ref.current;
      if (!menu || menu.contains(event.target)) return;
      if (!isTableColorPickerOpen()) {
        onClose();
        return;
      }
      const input = document.activeElement;
      input?.addEventListener("blur", closeAfterColorPicker, { once: true });
    };
    const onKey = (event) => {
      const buttons = [...(ref.current?.querySelectorAll("button.item:not(:disabled)") || [])];
      const index = buttons.indexOf(document.activeElement);
      if (event.key === "ArrowDown" && buttons.length) {
        event.preventDefault();
        buttons[(index + 1) % buttons.length].focus();
      } else if (event.key === "ArrowUp" && buttons.length) {
        event.preventDefault();
        buttons[(index - 1 + buttons.length) % buttons.length].focus();
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      } else if (event.key === "Tab") {
        onClose();
      }
    };
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="pr-table-menu"
      data-table-menu
      role="menu"
      style={{ left: box.left, top: box.top, maxHeight: box.maxHeight ? `${box.maxHeight}px` : undefined }}
    >
      {items.map((item, index) => {
        if (item === "-") return <hr key={`hr-${index}`} />;
        if (item.palette) {
          return (
            <FillPalette
              key={`pal-${index}`}
              label={item.label}
              current={item.current}
              accent={item.accent}
              onPick={(value) => {
                item.onPick(value);
                onClose();
              }}
              onCustomPick={(value) => {
                item.onPick(value);
              }}
            />
          );
        }
        return (
          <button
            key={`${item.label}-${index}`}
            type="button"
            role="menuitem"
            className={`item${item.danger ? " danger" : ""}`}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              onClose();
              item.fn();
            }}
          >
            {item.label}
            {item.key ? <small>{item.key}</small> : null}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}

const TableBlock = ({
  model,
  selected = false,
  readOnly = false,
  locked = false,
  lockedSelected = false,
  selectionKey = null,
  range = null,
  onRangeChange,
  onChange,
  onSelect,
  onActiveChange,
  onMeasure,
  onUndo,
  onRedo,
  onMovePointerDown,
  onCornerPointerDown,
  blockRef,
}) => {
  const localRef = useRef(null);
  const rootRef = blockRef || localRef;
  // Also written directly by edits, ahead of the parent's new model (see commit).
  const modelRef = useLatestRef(model);
  const selectionKeyRef = useLatestRef(selectionKey);
  const [active, setActive] = useState(null);
  // The cell a first click landed on while the table was not selected. That click only selects the table, so
  // the cell is not opened for editing, but it is still the cell the toolbar should change.
  const [picked, setPicked] = useState(null);
  const onActiveChangeRef = useLatestRef(onActiveChange);
  const onMeasureRef = useLatestRef(onMeasure);
  const historyRef = useLatestRef({ undo: onUndo, redo: onRedo });
  const pickedHere = picked && picked.row < model.rows.length && picked.col < (model.rows[0]?.length || 0) ? picked : null;
  const targetCell = active || pickedHere;
  const targetRow = targetCell?.row ?? null;
  const targetCol = targetCell?.col ?? null;
  useEffect(() => {
    onActiveChangeRef.current?.(targetRow == null ? null : { row: targetRow, col: targetCol });
  }, [targetRow, targetCol, onActiveChangeRef]);
  useEffect(() => {
    if (active) setPicked(null);
  }, [active]);
  const [typingCell, setTypingCell] = useState(null);
  const [menu, setMenu] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [resizeCol, setResizeCol] = useState(-1);
  const [resizeRow, setResizeRow] = useState(-1);
  const dragAnchor = useRef(null);
  const pendingMove = useRef(null);
  const typingCellRef = useRef(null);
  const moveDragRef = useLatestRef(onMovePointerDown);
  const rect = selectionRect(range);
  const count = selectionCount(range);
  const rows = model.rows.length;
  const cols = model.rows[0]?.length || 1;

  // Undo, redo or a delete can leave the table smaller than the selection. The selection moves to the nearest
  // cell that still exists, and a range shrinks (or clears when none of it is left), so a toolbar change can
  // never land on a cell that is gone or fall back to the whole table by accident.
  useEffect(() => {
    const lastRow = rows - 1;
    const lastCol = cols - 1;
    const rect = selectionRect(range);
    setActive((current) =>
      current && (current.row > lastRow || current.col > lastCol)
        ? { row: Math.min(current.row, lastRow), col: Math.min(current.col, lastCol) }
        : current,
    );
    setPicked((current) => (current && (current.row > lastRow || current.col > lastCol) ? null : current));
    if (rect && (rect.r1 > lastRow || rect.c1 > lastCol)) {
      onRangeChange?.(
        rect.r0 > lastRow || rect.c0 > lastCol
          ? null
          : {
              r1: Math.min(range.r1, lastRow),
              c1: Math.min(range.c1, lastCol),
              r2: Math.min(range.r2, lastRow),
              c2: Math.min(range.c2, lastCol),
            },
      );
    }
  }, [rows, cols, range, onRangeChange]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const sync = () => {
      const table = node.querySelector("table");
      if (!table) return;
      node.style.setProperty("--th", `${table.offsetHeight}px`);
      node.style.setProperty("--tw", `${table.offsetWidth}px`);
      onMeasureRef.current?.({ height: table.offsetHeight });
    };
    sync();
    if (typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    return () => observer.disconnect();
  }, [model, rootRef, onMeasureRef]);

  useEffect(() => {
    if (selected) return;
    setActive(null);
    setPicked(null);
    setMenu(null);
    setTypingCell(null);
    typingCellRef.current = null;
  }, [selected]);

  const commit = (next, meta) => {
    modelRef.current = next;
    onChange?.(next, meta);
  };
  const focusCell = (row, col, dims = { rows, cols }) => {
    const safeRow = Math.max(0, Math.min(dims.rows - 1, row));
    const safeCol = Math.max(0, Math.min(dims.cols - 1, col));
    setActive({ row: safeRow, col: safeCol });
  };

  useEffect(() => {
    if (!active || readOnly) return undefined;
    const node = rootRef.current?.querySelector(`.cell[data-r="${active.row}"][data-c="${active.col}"]`);
    if (!node || document.activeElement === node) return undefined;
    node.focus();
    return undefined;
  }, [active, readOnly, model, rootRef]);

  const changeAndFocus = (next, row, col) => {
    // The edit moves cells around, so a selected range no longer points at what the user picked.
    onRangeChange?.(null);
    commit(next, { focus: { row, col } });
    const dims = { rows: next.rows.length, cols: next.rows[0]?.length || 1 };
    window.setTimeout(() => focusCell(row, col, dims), 0);
  };

  const closeMenu = () => {
    setMenu(null);
    setHighlight(null);
  };

  const rowItems = (row) => [
    { palette: true, label: "Row color", current: model.fills[row], accent: model.accent, onPick: (value) => commit(setRowFill(model, row, value)) },
    "-",
    { label: "Insert row above", fn: () => changeAndFocus(insRow(model, row), row, 0) },
    { label: "Insert row below", key: `${MOD}+Enter`, fn: () => changeAndFocus(insRow(model, row + 1), row + 1, 0) },
    { label: "Duplicate row", fn: () => changeAndFocus(insRow(model, row + 1, row + 1), row + 1, 0) },
    "-",
    { label: "Move row up", disabled: row === 0, fn: () => changeAndFocus(moveRow(model, row, -1), row - 1, 0) },
    { label: "Move row down", disabled: row === rows - 1, fn: () => changeAndFocus(moveRow(model, row, 1), row + 1, 0) },
    "-",
    {
      label: "Delete row",
      danger: true,
      disabled: rows <= 1,
      fn: () => {
        const removed = delRow(model, row);
        if (!removed.ok) return;
        changeAndFocus(removed.model, Math.min(row, removed.model.rows.length - 1), 0);
      },
    },
  ];

  const colItems = (col) => [
    { palette: true, label: "Column color", current: model.cfills[col], accent: model.accent, onPick: (value) => commit(setColFill(model, col, value)) },
    "-",
    { label: "Insert column left", fn: () => changeAndFocus(insCol(model, col), 0, col) },
    { label: "Insert column right", fn: () => changeAndFocus(insCol(model, col + 1), 0, col + 1) },
    "-",
    { label: "Sort A to Z", fn: () => commit(sortCol(model, col, 1)) },
    { label: "Sort Z to A", fn: () => commit(sortCol(model, col, -1)) },
    "-",
    { label: "Move column left", disabled: col === 0, fn: () => changeAndFocus(moveCol(model, col, -1), 0, col - 1) },
    { label: "Move column right", disabled: col === cols - 1, fn: () => changeAndFocus(moveCol(model, col, 1), 0, col + 1) },
    "-",
    {
      label: "Delete column",
      danger: true,
      disabled: cols <= 1,
      fn: () => {
        const removed = delCol(model, col);
        if (!removed.ok) return;
        changeAndFocus(removed.model, 0, Math.min(col, removed.model.rows[0].length - 1));
      },
    },
  ];

  const cellItems = (row, col) => [
    {
      palette: true,
      label: "Cell color",
      current: model.cf[row][col],
      accent: model.accent,
      onPick: (value) => commit(setCellFill(model, row, col, value)),
    },
    { palette: true, label: "Row color", current: model.fills[row], accent: model.accent, onPick: (value) => commit(setRowFill(model, row, value)) },
    { palette: true, label: "Column color", current: model.cfills[col], accent: model.accent, onPick: (value) => commit(setColFill(model, col, value)) },
    "-",
    { label: "Insert row above", fn: () => changeAndFocus(insRow(model, row), row, col) },
    { label: "Insert row below", key: `${MOD}+Enter`, fn: () => changeAndFocus(insRow(model, row + 1), row + 1, col) },
    { label: "Insert column left", fn: () => changeAndFocus(insCol(model, col), row, col) },
    { label: "Insert column right", fn: () => changeAndFocus(insCol(model, col + 1), row, col + 1) },
    "-",
    { label: "Clear cell", fn: () => changeAndFocus(setCellHtml(model, row, col, ""), row, col) },
    "-",
    {
      label: "Delete row",
      danger: true,
      disabled: rows <= 1,
      fn: () => {
        const removed = delRow(model, row);
        if (removed.ok) changeAndFocus(removed.model, Math.min(row, removed.model.rows.length - 1), col);
      },
    },
    {
      label: "Delete column",
      danger: true,
      disabled: cols <= 1,
      fn: () => {
        const removed = delCol(model, col);
        if (removed.ok) changeAndFocus(removed.model, row, Math.min(col, removed.model.rows[0].length - 1));
      },
    },
  ];

  const rangeItems = () => {
    if (!rect || count < 2) return cellItems(active?.row || 0, active?.col || 0);
    const rowSpan = rect.r1 - rect.r0 + 1;
    const colSpan = rect.c1 - rect.c0 + 1;
    return [
      {
        palette: true,
        label: `Cell color · ${count} cells`,
        current: model.cf[rect.r0][rect.c0],
        accent: model.accent,
        onPick: (value) => commit(setRangeFill(model, rect, value)),
      },
      "-",
      { label: "Clear contents", fn: () => commit(clearRangeText(model, rect)) },
      "-",
      {
        label: rowSpan > 1 ? `Delete ${rowSpan} rows` : "Delete row",
        danger: true,
        disabled: rows - rowSpan < 1,
        fn: () => {
          let next = model;
          for (let row = rect.r1; row >= rect.r0; row -= 1) {
            const removed = delRow(next, row);
            if (!removed.ok) return;
            next = removed.model;
          }
          onRangeChange?.(null);
          commit(next);
        },
      },
      {
        label: colSpan > 1 ? `Delete ${colSpan} columns` : "Delete column",
        danger: true,
        disabled: cols - colSpan < 1,
        fn: () => {
          let next = model;
          for (let col = rect.c1; col >= rect.c0; col -= 1) {
            const removed = delCol(next, col);
            if (!removed.ok) return;
            next = removed.model;
          }
          onRangeChange?.(null);
          commit(next);
        },
      },
    ];
  };

  const openGrip = (event, kind, index) => {
    event.preventDefault();
    event.stopPropagation();
    const bounds = event.currentTarget.getBoundingClientRect();
    event.currentTarget.setAttribute("aria-expanded", "true");
    setHighlight(kind === "col" ? { kind, index } : { kind, index });
    setMenu({
      items: kind === "col" ? colItems(index) : rowItems(index),
      point: kind === "col" ? { x: bounds.left, y: bounds.bottom + 6 } : { x: bounds.right + 6, y: bounds.top },
      grip: event.currentTarget,
    });
  };

  // Drags a row's bottom edge. The row keeps the height it is dragged to as its minimum (text can still push it
  // taller). Page zoom scales both the pointer and the row, so the drag is measured in the row's own pixels.
  const onRowResizeDown = (event, row) => {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    const line = rootRef.current?.querySelectorAll("tr")?.[row];
    if (!line) return;
    const scale = line.getBoundingClientRect().height / (line.offsetHeight || 1) || 1;
    const startY = event.clientY;
    // A row that already has its own height starts from that number; an automatic row starts from its cells (see
    // automaticRowHeight), so neither path adds the border to the stored height.
    const startHeight = model.rowH?.[row] || automaticRowHeight(line);
    let latest = model;
    let started = false;
    setResizeRow(row);
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = "row-resize";
    const move = (moveEvent) => {
      const height = Math.round(
        Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, startHeight + (moveEvent.clientY - startY) / scale)),
      );
      if (latest.rowH?.[row] === height) return;
      latest = setRowHeight(model, row, height);
      commit(latest, started ? { live: true, quiet: true } : { live: true });
      started = true;
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.body.style.cursor = previousCursor;
      document.removeEventListener("pointercancel", up);
      setResizeRow(-1);
      if (started) commit(latest, { liveEnd: true });
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    // A touch the browser takes over (a gesture, a call) is cancelled, not released: end the drag the same way.
    document.addEventListener("pointercancel", up);
  };

  // Alt+Up / Alt+Down in a cell: the keyboard way to resize that cell's row (Shift for bigger steps).
  const nudgeRowHeight = (row, delta) => {
    const line = rootRef.current?.querySelectorAll("tr")?.[row];
    const current = model.rowH?.[row] || (line ? automaticRowHeight(line) : 0) || MIN_ROW_HEIGHT;
    const next = Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, Math.round(current + delta)));
    if (next !== model.rowH?.[row]) commit(setRowHeight(model, row, next));
  };

  const onColumnResizeDown = (event, col, edge) => {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    const table = rootRef.current?.querySelector("table");
    const width = table?.getBoundingClientRect().width || 1;
    const startX = event.clientX;
    const start = model.widths.slice();
    const a = start[col];
    const b = edge ? 0 : start[col + 1];
    let latest = model;
    let started = false;
    setResizeCol(col);
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = "col-resize";
    const move = (moveEvent) => {
      const delta = ((moveEvent.clientX - startX) / width) * 100;
      const widths = start.slice();
      if (edge) {
        if (cols < 2) return;
        const nextWidth = Math.max(1, Math.min(100 - (cols - 1), a + delta));
        const scale = (100 - nextWidth) / (100 - a || 1);
        for (let index = 0; index < cols - 1; index += 1) widths[index] = start[index] * scale;
        widths[col] = nextWidth;
      } else {
        const nextWidth = Math.max(1, Math.min(a + b - 1, a + delta));
        widths[col] = nextWidth;
        widths[col + 1] = a + b - nextWidth;
      }
      latest = { ...model, widths };
      if (!started) {
        started = true;
        commit(latest, { live: true });
      } else {
        commit(latest, { live: true, quiet: true });
      }
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.body.style.cursor = previousCursor;
      document.removeEventListener("pointercancel", up);
      setResizeCol(-1);
      if (started) commit(latest, { liveEnd: true });
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", up);
  };

  const onKeyDown = (event) => {
    const cell = event.target.closest?.(".cell");
    if (!cell || readOnly) return;
    // With a cell open for editing the browser would run its own undo, which knows nothing about the table's
    // edits (formatting, rows, typing runs). Undo and redo go to the editor's history instead.
    const shortcut = historyRef.current.undo ? historyShortcut(event) : null;
    if (shortcut) {
      event.preventDefault();
      event.stopPropagation();
      if (shortcut === "redo") historyRef.current.redo?.();
      else historyRef.current.undo();
      return;
    }
    const row = Number(cell.dataset.r);
    const col = Number(cell.dataset.c);
    if (event.altKey && !event.ctrlKey && !event.metaKey && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      nudgeRowHeight(row, (event.key === "ArrowDown" ? 1 : -1) * (event.shiftKey ? 20 : 4));
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      let nextRow = row;
      let nextCol = col + (event.shiftKey ? -1 : 1);
      if (nextCol < 0) {
        nextCol = cols - 1;
        nextRow -= 1;
      }
      if (event.shiftKey) {
        if (nextRow < 0) return;
        focusCell(nextRow, nextCol);
        return;
      }
      if (nextCol >= cols) {
        nextCol = 0;
        nextRow += 1;
      }
      if (nextRow >= rows) changeAndFocus(insRow(model, rows), nextRow, 0);
      else focusCell(nextRow, nextCol);
      return;
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      changeAndFocus(insRow(model, row + 1), row + 1, col);
      return;
    }
    if (event.shiftKey && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      event.preventDefault();
      window.getSelection()?.removeAllRanges();
      const current = range?.r1 === row && range?.c1 === col ? range : { r1: row, c1: col, r2: row, c2: col };
      const delta = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[event.key];
      onRangeChange?.({
        r1: current.r1,
        c1: current.c1,
        r2: Math.max(0, Math.min(rows - 1, current.r2 + delta[0])),
        c2: Math.max(0, Math.min(cols - 1, current.c2 + delta[1])),
      });
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (range) {
        onRangeChange?.(null);
        return;
      }
      cell.blur();
      setActive(null);
      setTypingCell(null);
      typingCellRef.current = null;
    }
  };

  const onPaste = (event) => {
    const cell = event.target.closest?.(".cell");
    if (!cell || readOnly) return;
    event.preventDefault();
    const text = event.clipboardData?.getData("text/plain") || "";
    if (!/\t/.test(text)) {
      document.execCommand("insertText", false, text);
      return;
    }
    const grid = text.replace(/\r/g, "").replace(/\n+$/, "").split("\n").map((line) => line.split("\t"));
    const row = Number(cell.dataset.r);
    const col = Number(cell.dataset.c);
    const next = applyPaste(model, row, col, grid);
    const width = Math.max(...grid.map((line) => line.length));
    changeAndFocus(next, Math.min(row + grid.length - 1, next.rows.length - 1), Math.min(col + width - 1, next.rows[0].length - 1));
  };

  useEffect(() => {
    const move = (event) => {
      const pending = pendingMove.current;
      if (pending) {
        if (!event.buttons) return;
        event.preventDefault();
        window.getSelection()?.removeAllRanges();
        const dx = event.clientX - pending.x;
        const dy = event.clientY - pending.y;
        if (Math.hypot(dx, dy) < 4) return;
        pendingMove.current = null;
        dragAnchor.current = null;
        const focused = document.activeElement;
        if (focused instanceof HTMLElement && focused.closest(".pr-table") === rootRef.current) focused.blur();
        setActive(null);
        setPicked(null);
        setTypingCell(null);
        typingCellRef.current = null;
        moveDragRef.current?.({
          clientX: pending.x,
          clientY: pending.y,
          currentX: event.clientX,
          currentY: event.clientY,
          screenX: pending.screenX,
          screenY: pending.screenY,
          preventDefault() {},
          stopPropagation() {},
        });
        return;
      }
      const anchor = dragAnchor.current;
      if (!anchor || !event.buttons) return;
      const hit = document.elementFromPoint?.(event.clientX, event.clientY)?.closest?.(".cell");
      if (!hit || hit.closest(".pr-table") !== rootRef.current) return;
      const row = Number(hit.dataset.r);
      const col = Number(hit.dataset.c);
      if (row === anchor.row && col === anchor.col) return;
      window.getSelection()?.removeAllRanges();
      onRangeChange?.({ r1: anchor.row, c1: anchor.col, r2: row, c2: col });
    };
    const up = () => {
      const pending = pendingMove.current;
      pendingMove.current = null;
      dragAnchor.current = null;
      if (!pending) return;
      if (!pending.focus) {
        setPicked({ row: pending.row, col: pending.col });
        return;
      }
      setActive({ row: pending.row, col: pending.col });
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
  }, [onRangeChange, rootRef, moveDragRef]);

  useEffect(() => {
    if (selected) {
      setTableSelection({ key: selectionKey, range });
      return undefined;
    }
    if (getTableSelection().key === selectionKey) setTableSelection({ key: null, range: null });
    return undefined;
  }, [selected, selectionKey, range]);

  useEffect(
    () => () => {
      if (getTableSelection().key === selectionKeyRef.current) setTableSelection({ key: null, range: null });
    },
    [selectionKeyRef],
  );

  return (
    <div
      ref={rootRef}
      className={`pr-table st-${model.style} fs-${model.size} al-${model.align}${selected ? " is-selected" : ""}${readOnly ? " is-readonly" : ""}${lockedSelected ? " is-locked-selected" : ""}`}
      style={{
        "--accent": model.accent,
        "--accent-8": mixWithWhite(model.accent, 0.08),
        "--accent-11": mixWithWhite(model.accent, 0.11),
        "--rowh": model.h ? `${model.h}px` : "1.5em",
        ...typographyCssVars(model.typography),
      }}
      onPointerDown={(event) => {
        if (readOnly) {
          // A locked table can still be selected so the toolbar can unlock it.
          if (locked) {
            event.preventDefault();
            onSelect?.();
          }
          return;
        }
        const cell = event.target.closest?.(".cell");
        if (!selected) {
          if (!event.target.closest?.("button, .rz, .rzr")) event.preventDefault();
          if (document.activeElement instanceof HTMLElement && document.activeElement.isContentEditable) {
            document.activeElement.blur();
          }
          setActive(null);
          setTypingCell(null);
          typingCellRef.current = null;
          onSelect?.();
          if (cell && event.button !== 2) {
            pendingMove.current = {
              x: event.clientX,
              y: event.clientY,
              screenX: event.screenX,
              screenY: event.screenY,
              row: Number(cell.dataset.r),
              col: Number(cell.dataset.c),
              focus: false,
            };
          }
          return;
        }
        onSelect?.();
        if (!cell || event.target.closest?.("button, .rz, .rzr")) return;
        const row = Number(cell.dataset.r);
        const col = Number(cell.dataset.c);
        if (event.button === 2) return;
        const anchorCell = active || pickedHere;
        if (event.shiftKey && anchorCell) {
          event.preventDefault();
          onRangeChange?.({ r1: anchorCell.row, c1: anchorCell.col, r2: row, c2: col });
          return;
        }
        const typingHere = typingCellRef.current?.row === row && typingCellRef.current?.col === col;
        // A plain click starts a new selection, so an earlier range (even a large one) ends here.
        if (typingHere) {
          setActive({ row, col });
          onRangeChange?.(null);
          if (event.pointerType !== "touch") dragAnchor.current = { row, col };
          return;
        }
        setTypingCell(null);
        typingCellRef.current = null;
        onRangeChange?.(null);
        pendingMove.current = {
          x: event.clientX,
          y: event.clientY,
          screenX: event.screenX,
          screenY: event.screenY,
          row,
          col,
          focus: true,
        };
      }}
      onContextMenu={(event) => {
        const cell = event.target.closest?.(".cell");
        if (!cell || readOnly) return;
        event.preventDefault();
        const row = Number(cell.dataset.r);
        const col = Number(cell.dataset.c);
        const inside = rect && row >= rect.r0 && row <= rect.r1 && col >= rect.c0 && col <= rect.c1 && count > 1;
        setMenu({
          items: inside ? rangeItems() : cellItems(row, col),
          point: { x: event.clientX, y: event.clientY },
        });
      }}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onClick={(event) => {
        if (readOnly) return;
        const insert = event.target.closest?.("[data-ins]");
        if (insert) {
          const at = Number(insert.dataset.at);
          if (insert.dataset.ins === "col") changeAndFocus(insCol(model, at), active?.row || 0, at);
          else changeAndFocus(insRow(model, at), at, active?.col || 0);
          return;
        }
        const add = event.target.closest?.("[data-add]");
        if (add) {
          if (add.dataset.add === "row") changeAndFocus(insRow(model, rows), rows, 0);
          else changeAndFocus(insCol(model, cols), model.headerRow ? 0 : 0, cols);
          return;
        }
        const grip = event.target.closest?.("[data-grip]");
        if (grip) openGrip(event, grip.dataset.grip, Number(grip.dataset.c ?? grip.dataset.r));
      }}
    >
      <table>
        <colgroup>
          {model.widths.map((width, index) => (
            <col key={`col-${index}`} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <tbody>
          {model.rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`} style={model.rowH?.[rowIndex] ? { "--rowh": `${model.rowH[rowIndex]}px` } : undefined}>
              {row.map((html, colIndex) => {
                const headerRow = model.headerRow && rowIndex === 0;
                const headerCol = model.headerCol && colIndex === 0;
                const Tag = headerRow || headerCol ? "th" : "td";
                const fill = cellFill(model, rowIndex, colIndex);
                const [background, foreground] = fill ? fillCSSPair(model, fill) : ["", ""];
                const highlighted = highlight && ((highlight.kind === "col" && highlight.index === colIndex) || (highlight.kind === "row" && highlight.index === rowIndex));
                const inRange = rect && count > 1 && rowIndex >= rect.r0 && rowIndex <= rect.r1 && colIndex >= rect.c0 && colIndex <= rect.c1;
                const className = [
                  headerRow ? "hr" : "",
                  headerCol ? "hc" : "",
                  fill ? "fill" : "",
                  active && active.row === rowIndex && active.col === colIndex ? "is-active" : "",
                  !active && pickedHere && pickedHere.row === rowIndex && pickedHere.col === colIndex ? "is-picked" : "",
                  highlighted ? "is-hl" : "",
                  inRange ? "is-selcell" : "",
                ].filter(Boolean).join(" ");
                const own = model.cellTypo?.[rowIndex]?.[colIndex];
                const cellStyle = own || fill
                  ? {
                      ...typographyCssVars(own),
                      // An explicit fill wins the background; a cell's own text color wins over the fill's.
                      ...(own?.background ? { backgroundColor: own.background } : null),
                      ...(fill ? { backgroundColor: background, color: foreground } : null),
                      ...(own?.color ? { color: own.color } : null),
                    }
                  : undefined;
                return (
                  <Tag
                    key={`cell-${rowIndex}-${colIndex}`}
                    className={className}
                    scope={headerRow ? "col" : headerCol ? "row" : undefined}
                    style={cellStyle}
                  >
                    {rowIndex === 0 && !readOnly ? (
                      <div className="cbar">
                        {colIndex === 0 ? (
                          <button type="button" className="ins start" data-ins="col" data-at="0" aria-label="Insert column before column 1" title="Insert column">+</button>
                        ) : null}
                        <button
                          type="button"
                          className="grip"
                          data-grip="col"
                          data-c={colIndex}
                          aria-label={`Column ${colIndex + 1} options`}
                          aria-haspopup="menu"
                          aria-expanded="false"
                        />
                        <button type="button" className="ins" data-ins="col" data-at={colIndex + 1} aria-label={`Insert column after column ${colIndex + 1}`} title="Insert column">+</button>
                      </div>
                    ) : null}
                    {rowIndex === 0 && !readOnly ? (
                      <span
                        className={`rz${colIndex === cols - 1 ? " edge" : ""}${resizeCol === colIndex ? " is-on" : ""}`}
                        data-c={colIndex}
                        title="Drag to resize"
                        onPointerDown={(event) => onColumnResizeDown(event, colIndex, colIndex === cols - 1)}
                      />
                    ) : null}
                    {colIndex === 0 && !readOnly ? (
                      <span
                        className={`rzr${resizeRow === rowIndex ? " is-on" : ""}`}
                        data-rzr={rowIndex}
                        title="Drag to resize the row. Double-click for automatic height. Alt+Up or Alt+Down in a cell also resizes it."
                        onPointerDown={(event) => onRowResizeDown(event, rowIndex)}
                        onDoubleClick={() => {
                          if (model.rowH?.[rowIndex]) commit(setRowHeight(model, rowIndex, null));
                        }}
                      />
                    ) : null}
                    {colIndex === 0 && !readOnly ? (
                      <div className="rbar">
                        {rowIndex === 0 ? (
                          <button type="button" className="ins start" data-ins="row" data-at="0" aria-label="Insert row above row 1" title="Insert row">+</button>
                        ) : null}
                        <button
                          type="button"
                          className="grip"
                          data-grip="row"
                          data-r={rowIndex}
                          aria-label={`Row ${rowIndex + 1} options`}
                          aria-haspopup="menu"
                          aria-expanded="false"
                        />
                        <button type="button" className="ins" data-ins="row" data-at={rowIndex + 1} aria-label={`Insert row below row ${rowIndex + 1}`} title="Insert row">+</button>
                      </div>
                    ) : null}
                    <CellView
                      html={html}
                      blank={!normalizeCellHtml(html)}
                      editable={!readOnly && active?.row === rowIndex && active?.col === colIndex}
                      row={rowIndex}
                      col={colIndex}
                      placeholder={headerRow ? `Heading ${colIndex + 1}` : ""}
                      label={`Row ${rowIndex + 1}, column ${colIndex + 1}`}
                      typing={typingCell?.row === rowIndex && typingCell?.col === colIndex}
                      onFocus={() => {
                        onSelect?.();
                        setActive({ row: rowIndex, col: colIndex });
                      }}
                      onInput={(value) => {
                        const previous = modelRef.current?.rows?.[rowIndex]?.[colIndex] || "";
                        if (htmlToText(value) !== htmlToText(previous)) {
                          typingCellRef.current = { row: rowIndex, col: colIndex };
                          setTypingCell({ row: rowIndex, col: colIndex });
                        }
                        const next = setCellHtml(modelRef.current, rowIndex, colIndex, value);
                        commit(next, { typing: true });
                      }}
                    />
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {readOnly ? null : (
        <>
          <button type="button" className="addrow" data-add="row" aria-label="Add row at bottom">+ Add row</button>
          <button type="button" className="addcol" data-add="col" aria-label="Add column at right" title="Add column">+</button>
          <span
            className="corner"
            title="Drag to resize the table"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onCornerPointerDown?.(event);
            }}
          />
          <button
            type="button"
            className="mover"
            aria-label="Move this table"
            title="Drag to move this table"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              // Grabbing the whole table also targets the whole table: toolbar changes are table-wide again.
              if (document.activeElement instanceof HTMLElement && document.activeElement.isContentEditable) {
                document.activeElement.blur();
              }
              setActive(null);
              setPicked(null);
              setTypingCell(null);
              typingCellRef.current = null;
              onRangeChange?.(null);
              onMovePointerDown?.(event);
            }}
          />
        </>
      )}
      {menu ? (
        <TableMenu
          items={menu.items}
          point={menu.point}
          onClose={() => {
            menu.grip?.setAttribute("aria-expanded", "false");
            closeMenu();
          }}
        />
      ) : null}
    </div>
  );
};

export default TableBlock;
